import { AFK_THRESHOLD_DEFAULT, AUTO_START_SECONDS_DEFAULT, CELL_SIZE, Speed, TICK_MS } from '@shared/constants.js';
import { Direction, Role, ScreenState, UnlockId } from '@shared/types.js';
import type { Crash, Grid, Player } from '@shared/types.js';

export const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

export interface GameState {
  state: ScreenState;
  online: boolean;
  onlinePlayerId: number | null;
  onlineRole: Role | null;
  isHost: boolean;
  cellSize: number;
  cols: number;
  rows: number;
  width: number;
  height: number;
  grid: Grid;
  players: Player[];
  playerById: Record<number, Player>;
  crashes: Crash[];
  crashTick: number;
  scores: Record<number, number>;
  names: string[];
  tickTimer: ReturnType<typeof setInterval> | null;
  playerCount: number;
  playerIsHuman: boolean[];
  speed: Speed;
  arenaSpeed: Speed;
  arenaAutoStart: boolean;
  arenaUnlock: UnlockId | null;
  arenaAfkThreshold: number;
  arenaAutoStartSeconds: number;
  /** Pending turn buffer for the local human (depth MAX_DIR_QUEUE), drained per tick (#61) */
  localDirQueue: Direction[];
  _botNames: string[];
  lastTickTime: number;
  tickMs: number;
  prevPositions: Record<number, { x: number; y: number }>;
  pendingWinner: Player | null;
  winnerId: number | null;
  speedRampActive: boolean;
  speedRampIndex: number;
  speedRampTickCount: number;
  /** Recent trail positions per player for fire trail effect */
  fireTrail: Record<number, Array<{ x: number; y: number }>>;
  /** Jump: pending jump request for next tick */
  jumpPending: boolean;
  /** Jump: remaining jumps this game */
  jumpsLeft: number;
  /** Jump: ticks remaining in current jump (2 = airborne, 1 = landing, 0 = not jumping) */
  jumpingTicks: number;
  /** Jump: player ID currently jumping */
  jumpingPlayerId: number | null;
  /** Spectator was auto-seated as a competitor at game end (cleared when the next game starts) */
  promotedFromSpectator: boolean;
  /** performance.now() when the countdown began — anchors the "YOU" indicator's fixed cycle count */
  youIndicatorStart: number;
}

export const game: GameState = {
  state: ScreenState.HOME,
  online: false,
  onlinePlayerId: null,
  onlineRole: null,
  isHost: false,
  cellSize: CELL_SIZE,
  cols: 0,
  rows: 0,
  width: 0,
  height: 0,
  grid: [],
  players: [],
  playerById: {},
  crashes: [],
  crashTick: 0,
  scores: {},
  names: [],
  tickTimer: null,
  playerCount: 2,
  speed: Speed.Normal,
  arenaSpeed: Speed.Slowest,
  arenaAutoStart: false,
  arenaUnlock: null,
  arenaAfkThreshold: AFK_THRESHOLD_DEFAULT,
  arenaAutoStartSeconds: AUTO_START_SECONDS_DEFAULT,
  localDirQueue: [],
  _botNames: [],
  playerIsHuman: [true, false, false, false, false, false],
  lastTickTime: 0,
  tickMs: TICK_MS,
  prevPositions: {},
  pendingWinner: null,
  winnerId: null,
  speedRampActive: false,
  speedRampIndex: 0,
  speedRampTickCount: 0,
  fireTrail: {},
  jumpPending: false,
  jumpsLeft: 0,
  jumpingTicks: 0,
  jumpingPlayerId: null,
  promotedFromSpectator: false,
  youIndicatorStart: 0,
};

export interface DomEls {
  header: HTMLElement;
  hint: HTMLElement;
  overlay: HTMLElement;
  message: HTMLElement;
  subMessage: HTMLElement;
  optionsPanel: HTMLElement;
  optionsHomeBtn: HTMLElement;
  playerList: HTMLElement;
  countDisplay: HTMLElement;
  countMinus: HTMLElement;
  countPlus: HTMLElement;
  startBtn: HTMLElement;
  pauseBtn: HTMLElement;
  joinArenaBtn: HTMLElement;
  localPlayBtn: HTMLElement;
  homeBtn: HTMLElement;
  playAgainBtn: HTMLElement;
  spectateBtn: HTMLElement;
  arenaPanel: HTMLElement;
  arenaList: HTMLElement;
  arenaSpectatorList: HTMLElement;
  arenaRoleNotice: HTMLElement;
  arenaStartRow: HTMLElement;
  arenaHomeBtn: HTMLElement;
  nameInput: HTMLInputElement;
  onlineStatus: HTMLElement;
  dpadArea: HTMLElement;
  speedToggle: HTMLElement;
  arenaSpeedToggle: HTMLButtonElement;
  autoStartDisplay: HTMLElement;
  spectatingLabel: HTMLElement;
  version: HTMLElement;
  arenaActionButtons: HTMLElement;
  optionsBtnAction: HTMLElement;
  arenaAutoStartToggle: HTMLButtonElement;
  arenaStartBtn: HTMLButtonElement;
  arenaReadyBtn: HTMLButtonElement;
  arenaSpectateBtn: HTMLButtonElement;
  navOverlay: HTMLElement;
  navResumeBtn: HTMLElement;
  navLobbyBtn: HTMLElement;
  navHomeBtn: HTMLElement;
  navEndGameBtn: HTMLElement;
}
